import { isValid, parse, startOfDay } from "date-fns";

import {
	Cluster,
	Criteria,
	CriteriaType,
	Property
} from "trajectory/TrajectoryService";

import { ScoreAggregation } from "./ScoreAggregation";
import { ScoreResolution } from "./ScoreResolution";

// Colour of the "Behaald" bucket on the dashboard widgets: competences marked
// achieved or offered without a numeric score.
export const ACHIEVED_COLOUR = "#1e88e5";

// score: the level the pie counts (under AVERAGE the mean rounded to the
// nearest level, ties up); value: the unrounded number the legend averages.
// The two are equal under LATEST.
export type ResolvedScore =
	| { kind: "score"; score: number; value: number }
	| { kind: "achieved" }
	| { kind: "none" };

// The parsed score of one snapshot, or undefined when there is none. Missing,
// blank and non-integer values all count as "no score" so callers keep falling
// back through the history.
export function scoreOf(snapshot: Property | undefined): number | undefined {
	const value = snapshot?.score;
	if (value === undefined || value === null || value.trim() === "") {
		return undefined;
	}

	const score = Number.parseInt(value.trim(), 10);
	return Number.isNaN(score) ? undefined : score;
}

const SNAPSHOT_DATE_FORMAT = "dd/MM/yyyy";

// The day a snapshot belongs to: its own "date" when it carries a parseable
// one (the evaluation date the teacher picked), else the local day it was
// recorded on (the map key, stamped by the browser when it was typed).
export function effectiveDate(
	key: string,
	snapshot: Property | undefined
): Date {
	const value = snapshot?.date?.trim();
	if (value) {
		const parsed = parse(value, SNAPSHOT_DATE_FORMAT, new Date());
		if (isValid(parsed)) {
			return startOfDay(parsed);
		}
	}

	// A key that does not parse cannot be placed; like the backend, never
	// drop such a snapshot (and never hand an Invalid Date to a caller).
	const recorded = new Date(key);
	return isValid(recorded) ? startOfDay(recorded) : new Date(0);
}

// Whether the snapshot belongs to the cutoff day or an earlier one.
export function isOnOrBefore(
	key: string,
	snapshot: Property | undefined,
	asOf: Date
): boolean {
	return effectiveDate(key, snapshot).getTime() <= startOfDay(asOf).getTime();
}

function isCluster(criterion: Criteria): criterion is Cluster {
	return criterion.type === CriteriaType.CLUSTER;
}

// The distinct effective dates over a criteria tree, competences and clusters
// alike, ascending; the as-of picker marks them.
export function evaluationDates(criteria: Criteria[]): Date[] {
	const dates = new Map<number, Date>();

	const collect = (nodes: Criteria[]): void => {
		nodes.forEach(node => {
			const history = node.properties ?? {};
			Object.keys(history).forEach(key => {
				const date = effectiveDate(key, history[key]);
				dates.set(date.getTime(), date);
			});

			if (isCluster(node)) {
				collect(node.subsections);
			}
		});
	};
	collect(criteria);

	return [...dates.values()].sort((a, b) => a.getTime() - b.getTime());
}

// The snapshot history newest-first, cut off at a day when one is given. Keys
// are ISO timestamps, which sort chronologically as strings.
export function snapshotsNewestFirst(
	criterion: Criteria,
	asOf?: Date
): Property[] {
	const history = criterion.properties ?? {};
	return Object.keys(history)
		.filter(
			key => asOf === undefined || isOnOrBefore(key, history[key], asOf)
		)
		.sort()
		.reverse()
		.map(date => history[date]);
}

// The newest snapshot. latestProperties is consulted first because the
// evaluation editor updates it before properties, so unsaved edits count
// exactly as they do today. Under a cutoff it is ignored: an unsaved edit has
// no place in a historical view, so the newest surviving snapshot is the one.
export function latestSnapshot(
	criterion: Criteria,
	asOf?: Date
): Property | undefined {
	if (asOf !== undefined) {
		return snapshotsNewestFirst(criterion, asOf)[0];
	}

	const latest = criterion.latestProperties
		? Object.values(criterion.latestProperties)[0]
		: undefined;
	return latest ?? snapshotsNewestFirst(criterion)[0];
}

// The score of the newest snapshot that carries one. Falls back through the
// history so a later score-less "behaald" pass does not hide an earlier score.
// A document with an empty history but a set latestProperties still resolves
// through that single snapshot.
export function lastScore(
	criterion: Criteria,
	asOf?: Date
): number | undefined {
	const snapshots = snapshotsNewestFirst(criterion, asOf);
	if (snapshots.length === 0) {
		return scoreOf(latestSnapshot(criterion, asOf));
	}

	for (const snapshot of snapshots) {
		const score = scoreOf(snapshot);
		if (score !== undefined) {
			return score;
		}
	}

	return undefined;
}

// The unweighted mean of every snapshot that carries a score. Score-less
// snapshots (a behaald pass, a remark) do not participate, two scores on the
// same day each count once, and under a cutoff only the surviving snapshots
// enter. The empty-history fallback mirrors lastScore, so a document with only
// latestProperties still resolves.
export function meanScore(
	criterion: Criteria,
	asOf?: Date
): number | undefined {
	const snapshots = snapshotsNewestFirst(criterion, asOf);
	if (snapshots.length === 0) {
		return scoreOf(latestSnapshot(criterion, asOf));
	}

	const scores: number[] = [];
	for (const snapshot of snapshots) {
		const score = scoreOf(snapshot);
		if (score !== undefined) {
			scores.push(score);
		}
	}

	if (scores.length === 0) {
		return undefined;
	}

	return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}

// Whether the newest snapshot marks the competence as achieved ("behaald") or
// offered; both count as "Behaald".
export function isMarkedAchieved(criterion: Criteria, asOf?: Date): boolean {
	const latest = latestSnapshot(criterion, asOf);
	return latest?.achieved === "true" || latest?.offered === "true";
}

// A "trailing behaald": the newest snapshot is flagged and has no score. On
// purpose the simple rule: a flag the editor carried over from an earlier
// snapshot counts too, which is also what the editor itself displays.
export function hasTrailingAchieved(criterion: Criteria, asOf?: Date): boolean {
	return (
		isMarkedAchieved(criterion, asOf) &&
		scoreOf(latestSnapshot(criterion, asOf)) === undefined
	);
}

// The single frontend implementation of the rule (the backend twin is
// com.trajectx.trajectory.EffectiveScore). The mode decides the behaald
// question: a newest snapshot that carries a score is never a trailing
// behaald, never-scored flagged competences are "achieved" in both modes, and
// the modes only differ for a trailing behaald that follows an earlier score.
// The aggregation then decides which number a scored competence contributes,
// its newest score or the mean of all of them; the behaald rule is untouched,
// so the two compose. With a cutoff the rule runs over the history as it stood
// on that day. Under LATEST (the default) the result is exactly what it was
// before #633.
export function resolveScore(
	criterion: Criteria,
	resolution: ScoreResolution | undefined,
	asOf?: Date,
	aggregation?: ScoreAggregation
): ResolvedScore {
	const mode = resolution ?? ScoreResolution.LAST_SCORE;
	const how = aggregation ?? ScoreAggregation.LATEST;
	if (
		mode === ScoreResolution.TRAILING_ACHIEVED &&
		hasTrailingAchieved(criterion, asOf)
	) {
		return { kind: "achieved" };
	}

	if (how === ScoreAggregation.AVERAGE) {
		const mean = meanScore(criterion, asOf);
		if (mean !== undefined) {
			return { kind: "score", score: Math.round(mean), value: mean };
		}
	} else {
		const score = lastScore(criterion, asOf);
		if (score !== undefined) {
			return { kind: "score", score: score, value: score };
		}
	}

	if (isMarkedAchieved(criterion, asOf)) {
		return { kind: "achieved" };
	}

	return { kind: "none" };
}
